import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { fmtDate, fmtHours, monthRange } from '../utils/helpers';

export default function ReportPilot() {
  const [data,        setData]        = useState(null);
  const [instructors, setInstructors] = useState([]);
  const [students,    setStudents]    = useState([]);
  const [filters,     setFilters]     = useState(monthRange());
  const [loading,     setLoading]     = useState(false);

  useEffect(() => {
    api.instructors.list().then(setInstructors).catch(() => {});
    api.students.list().then(setStudents).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [filters]);

  async function load() {
    setLoading(true);
    try { setData(await api.reports.pilotTime(filters)); } catch {}
    setLoading(false);
  }

  function setF(k, v) { setFilters(f => ({ ...f, [k]: v || undefined })); }

  const showPic = !filters.seat || filters.seat === 'pic';
  const showSic = !filters.seat || filters.seat === 'sic';

  function DetailTable({ rows, seat }) {
    if (!rows || rows.length === 0) return null;
    return (
      <div className="card">
        <div className="card-header">
          <h2>{seat} Session Detail</h2>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Pilot</th>
                  <th>Instructor</th>
                  <th>Training Type</th>
                  <th>Hobbs Start</th>
                  <th>Hobbs Stop</th>
                  <th>Sim Time</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td>{fmtDate(r.date)}</td>
                    <td style={{fontWeight:600}}>
                      {r.pilot_name}
                      {seat === 'PIC' && r.single_pilot ? <span style={{fontSize:11,color:'#888',marginLeft:4}}>(solo)</span> : null}
                    </td>
                    <td>{r.instructor_name}</td>
                    <td><span className="badge badge-type">{r.training_type}</span></td>
                    <td>{r.hobbs_start}</td>
                    <td>{r.hobbs_stop}</td>
                    <td style={{fontWeight:600, color:'#1A2E5A'}}>{fmtHours(r.sim_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  function SummaryTable({ rows, seat }) {
    if (!rows || rows.length === 0) return null;
    return (
      <div className="card">
        <div className="card-header"><h2>Summary — {seat}</h2></div>
        <div className="card-body" style={{ padding: 0 }}>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Pilot</th>
                  <th>Sessions</th>
                  <th>Sim Hours</th>
                  <th>First Session</th>
                  <th>Last Session</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    <td style={{fontWeight:600}}>{r.pilot_name}</td>
                    <td>{r.session_count}</td>
                    <td style={{fontWeight:700, color:'#1A2E5A'}}>{fmtHours(r.total_sim_hours)}</td>
                    <td>{fmtDate(r.first_session)}</td>
                    <td>{fmtDate(r.last_session)}</td>
                  </tr>
                ))}
                <tr className="total-row" style={{background:'#f0f4fa'}}>
                  <td style={{fontWeight:700}}>TOTAL</td>
                  <td style={{fontWeight:700}}>{rows.reduce((a,r) => a + r.session_count, 0)}</td>
                  <td style={{fontWeight:700, color:'#1A2E5A'}}>{fmtHours(rows.reduce((a,r) => a + r.total_sim_hours, 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="section-title">Training Report</div>

      <div className="filter-bar">
        <div className="form-group">
          <label>From</label>
          <input type="date" value={filters.from || ''} onChange={e => setF('from', e.target.value)} />
        </div>
        <div className="form-group">
          <label>To</label>
          <input type="date" value={filters.to || ''} onChange={e => setF('to', e.target.value)} />
        </div>
        <div className="form-group">
          <label>By Person</label>
          <select value={filters.pilot || ''} onChange={e => setF('pilot', e.target.value)}>
            <option value="">All Pilots</option>
            <optgroup label="Instructors">
              {instructors.map(i => (
                <option key={`instructor:${i.id}`} value={`instructor:${i.id}`}>{i.name}</option>
              ))}
            </optgroup>
            <optgroup label="Students">
              {students.map(s => (
                <option key={`student:${s.id}`} value={`student:${s.id}`}>{s.name}</option>
              ))}
            </optgroup>
          </select>
        </div>
        <div className="form-group">
          <label>Seat</label>
          <select value={filters.seat || ''} onChange={e => setF('seat', e.target.value)}>
            <option value="">All Seats</option>
            <option value="pic">PIC Only</option>
            <option value="sic">SIC Only</option>
          </select>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => setFilters(monthRange())}>This Month</button>
        <button className="btn btn-secondary btn-sm" onClick={() => {
          const y = new Date().getFullYear();
          setFilters({ from: `${y}-01-01`, to: `${y}-12-31` });
        }}>This Year</button>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()}>Print / PDF</button>
      </div>

      {loading && <div className="empty">Loading...</div>}

      {data && (
        <>
          {showPic && data.pic_summary?.length > 0 && <SummaryTable rows={data.pic_summary} seat="Pilot in Command (PIC)" />}
          {showSic && data.sic_summary?.length > 0 && <SummaryTable rows={data.sic_summary} seat="Second in Command (SIC)" />}

          {data.pic_summary?.length === 0 && data.sic_summary?.length === 0 && (
            <div className="empty">No flight time data for selected period</div>
          )}

          {showPic && <DetailTable rows={data.pic_detail} seat="PIC" />}
          {showSic && <DetailTable rows={data.sic_detail} seat="SIC" />}
        </>
      )}
    </div>
  );
}
